import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Typography from "./ui/Typography";
import ArchiveSearchInput from "./archive-search/ArchiveSearchInput";
import ArchiveSearchResults from "./archive-search/ArchiveSearchResults";
import ArchiveSearchTagBar from "./archive-search/ArchiveSearchTagBar";
import { useArchiveDiscoverySearch } from "../hooks/useArchiveDiscoverySearch";
import { applyWatchlistBoost, pickArchiveSurpriseShow } from "../hooks/archiveSearchRanking";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import useWatchlist from "../hooks/useWatchlist";
import {
  clampSearchQuery,
  getShowsLikeAnchorShow,
  normalizeSearchText,
  parseArchiveSearchUrl,
  shouldSubmitSemanticSearch,
  titleMatchesByPrefix,
} from "../services/search/discoverySearch";
import { getArchiveTastePrompts } from "../services/search/archiveTastePrompts";
import {
  ARCHIVE_SEARCH_MODES,
  isSemanticMode,
  isStaleSemanticReason,
} from "../services/search/archiveSearchProtocol";
import { navigateTo } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";
import { cancelIdleWork, requestIdleWork } from "../utils/idleCallback";

const SEARCH_MIN_LENGTH = 2;
const POPULAR_TAG_COUNT = 4;
const SUGGESTION_SHOW_LIMIT = 4;
const SUGGESTION_TAG_LIMIT = 3;
const LEXICAL_SEARCH_DEBOUNCE_MS = 250;

function buildSuggestions(index, query, selectedTags = []) {
  if (!index || normalizeSearchText(query).length < SEARCH_MIN_LENGTH) {
    return [];
  }
  const normalizedQuery = normalizeSearchText(query);
  const suggestions = [];

  for (const tag of index.tags || []) {
    if (tag._suggestionText.includes(normalizedQuery)) {
      suggestions.push({
        id: `tag:${tag.slug}`,
        type: "tag",
        label: tag.tag,
        normalizedLabel: tag._normalizedLabel,
        meta: `${tag.count} ${tag.count === 1 ? "show" : "shows"}`,
        selected: selectedTags.includes(tag.slug),
        slug: tag.slug,
      });
    }
  }

  for (const show of index.shows || []) {
    if (titleMatchesByPrefix(show._normalizedLabel, normalizedQuery)) {
      suggestions.push({
        id: `show:${show.slug}`,
        type: "show",
        label: show.title,
        normalizedLabel: show._normalizedLabel,
        meta: [show.source, show.monthLabel].filter(Boolean).join(" / "),
        slug: show.slug,
      });
    }
  }

  const ranked = suggestions
    .sort((left, right) => {
      const leftExact = left.normalizedLabel === normalizedQuery ? 1 : 0;
      const rightExact = right.normalizedLabel === normalizedQuery ? 1 : 0;
      if (leftExact !== rightExact) return rightExact - leftExact;
      if (left.type !== right.type) return left.type === "show" ? -1 : 1;
      return left.label.localeCompare(right.label);
    });
  // Cap each kind so a common word's many tags cannot push the titles out of the list.
  const shown = [
    ...ranked.filter((suggestion) => suggestion.type === "show").slice(0, SUGGESTION_SHOW_LIMIT),
    ...ranked.filter((suggestion) => suggestion.type === "tag").slice(0, SUGGESTION_TAG_LIMIT),
  ];

  // Each show is followed by a "Shows like" option, so the keyboard reaches it with the arrow keys.
  return shown.flatMap((suggestion) => (
    suggestion.type === "show"
      ? [suggestion, {
        id: `shows-like:${suggestion.slug}`,
        type: "shows-like",
        label: `Shows like ${suggestion.label}`,
        showTitle: suggestion.label,
        slug: suggestion.slug,
      }]
      : [suggestion]
  ));
}

const ArchiveDiscoverySearch = ({ children, search = "" }) => {
  const comboboxId = useId();
  const listboxId = `${comboboxId}-listbox`;
  const statusId = `${comboboxId}-status`;
  const inputRef = useRef(null);
  const hydratedSearchRef = useRef(null);
  const analyticsResultRef = useRef("");
  const pendingHydrationSearchRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const {
    clearSearch,
    ensureIndex,
    fallbackReason,
    index,
    indexStatus,
    isSearchActive,
    runSearch,
    searchResult,
    searchStatus,
    setSearchState,
    state,
    unknownSemanticSlugs,
  } = useArchiveDiscoverySearch();
  const { entries: watchlistEntries, isInWatchlist, toggleWatchlist } = useWatchlist();

  useEffect(() => {
    setInputValue(state.query || "");
  }, [state.query]);

  useEffect(() => {
    const handle = requestIdleWork(() => ensureIndex().catch(() => {}), { timeout: 2500 });
    return () => cancelIdleWork(handle);
  }, [ensureIndex]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sourceSearch = typeof search === "string" ? search : window.location.search || "";
    if (hydratedSearchRef.current === sourceSearch) return;
    if (pendingHydrationSearchRef.current === sourceSearch) return;
    const nextState = parseArchiveSearchUrl(sourceSearch);
    if (nextState.query || nextState.selectedTags?.length || nextState.mode === ARCHIVE_SEARCH_MODES.SEMANTIC) {
      pendingHydrationSearchRef.current = sourceSearch;
      let started = false;
      const timer = window.setTimeout(() => {
        started = true;
        runSearch(nextState, { semantic: nextState.mode === ARCHIVE_SEARCH_MODES.SEMANTIC ? "force" : "never" })
          .then((result) => {
            if (result !== null) {
              hydratedSearchRef.current = sourceSearch;
            }
          })
          .catch(() => {})
          .finally(() => {
            if (pendingHydrationSearchRef.current === sourceSearch) {
              pendingHydrationSearchRef.current = null;
            }
          });
      }, 0);
      return () => {
        window.clearTimeout(timer);
        if (!started && pendingHydrationSearchRef.current === sourceSearch) {
          pendingHydrationSearchRef.current = null;
        }
      };
    }
    return undefined;
  }, [runSearch, search]);

  const popularTags = useMemo(() => (index?.tags || []).slice(0, POPULAR_TAG_COUNT), [index]);
  const suggestions = useMemo(
    () => buildSuggestions(index, inputValue, state.selectedTags || []),
    [index, inputValue, state.selectedTags]
  );
  const selectedTagDetails = useMemo(
    () => (state.selectedTags || [])
      .map((slug) => index?.tagsBySlug?.get(slug) || { slug, tag: slug })
      .filter(Boolean),
    [index, state.selectedTags]
  );
  const placeholderPrompt = getArchiveTastePrompts("homepageLowerPromo")[0] || "slow-burn dread";
  const boostedResults = useMemo(
    () => applyWatchlistBoost(searchResult?.results || [], watchlistEntries, { sort: state.sort }),
    [searchResult?.results, state.sort, watchlistEntries]
  );
  const results = boostedResults;
  const showSuggestions = suggestionsOpen && suggestions.length > 0;
  const canSubmitSearch =
    normalizeSearchText(inputValue).length >= SEARCH_MIN_LENGTH || selectedTagDetails.length > 0;
  const semanticFallback = Boolean(
    fallbackReason && !isStaleSemanticReason(fallbackReason) && state.mode === ARCHIVE_SEARCH_MODES.SEMANTIC
  );
  const showsLikeAnchor = useMemo(
    () => (index && isSemanticMode(searchResult?.mode) && !fallbackReason
      ? getShowsLikeAnchorShow(index, state.query)
      : null),
    [fallbackReason, index, searchResult?.mode, state.query]
  );
  const discoveryPrompt = searchStatus === "semantic-loading" ? "Searching by meaning" : "Search by meaning";
  const showBlockingLoading = searchStatus === "semantic-loading" && results.length === 0;

  useEffect(() => {
    if (!searchResult || searchStatus !== "ready") return;
    const signature = [
      state.mode,
      normalizeSearchText(state.query).split(" ").filter(Boolean).length,
      searchResult.results?.length || 0,
      searchResult.topScore || 0,
      fallbackReason || "",
    ].join("|");
    if (analyticsResultRef.current === signature) return;
    analyticsResultRef.current = signature;
    const payload = {
      token_count: normalizeSearchText(state.query).split(" ").filter(Boolean).length,
      selected_tags: (state.selectedTags || []).length,
      mode: searchResult.mode || state.mode,
      top_score: searchResult.topScore || 0,
      fallback_reason: fallbackReason || undefined,
    };
    if ((searchResult.results || []).length === 0) {
      trackEvent("archive_search_zero_results", payload);
    }
    if (searchResult.mode === ARCHIVE_SEARCH_MODES.SEMANTIC || searchResult.mode === ARCHIVE_SEARCH_MODES.SEMANTIC_STALE) {
      trackEvent("archive_search_semantic_used", payload);
    }
  }, [fallbackReason, searchResult, searchStatus, state.mode, state.query, state.selectedTags]);

  const prefetchIndex = useCallback(() => {
    ensureIndex().catch(() => {});
  }, [ensureIndex]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const clearSearchInput = useCallback(() => {
    setInputValue("");
    clearSearch();
    focusInput();
  }, [clearSearch, focusInput]);

  const surpriseMe = useCallback(() => {
    const surpriseShow = pickArchiveSurpriseShow({
      index,
      results,
      query: state.query,
      selectedTags: state.selectedTags || [],
    });
    if (!surpriseShow?.slug) {
      return;
    }
    trackEvent("archive_search_surprise_me", {
      show_slug: surpriseShow.slug,
      token_count: normalizeSearchText(state.query).split(" ").filter(Boolean).length,
      selected_tags: (state.selectedTags || []).length,
    });
    navigateTo(`/show/${surpriseShow.slug}`);
  }, [index, results, state.query, state.selectedTags]);

  const runLexical = useCallback((nextQuery, nextTags = state.selectedTags || [], nextSort = state.sort) => {
    const query = clampSearchQuery(nextQuery);
    setSearchState({ query, selectedTags: nextTags, mode: ARCHIVE_SEARCH_MODES.LEXICAL, sort: nextSort });
    if (normalizeSearchText(query).length >= SEARCH_MIN_LENGTH || nextTags.length > 0) {
      runSearch({ query, selectedTags: nextTags, mode: ARCHIVE_SEARCH_MODES.LEXICAL, sort: nextSort }, { semantic: "never" }).catch(() => {});
    }
  }, [runSearch, setSearchState, state.selectedTags, state.sort]);

  const scheduleLexical = useDebouncedCallback((nextQuery, nextTags = state.selectedTags || [], nextSort = state.sort) => {
    const query = clampSearchQuery(nextQuery);
    if (normalizeSearchText(query).length < SEARCH_MIN_LENGTH && nextTags.length === 0) {
      return;
    }
    runLexical(query, nextTags, nextSort);
  }, LEXICAL_SEARCH_DEBOUNCE_MS);

  const submitSemantic = useCallback(() => {
    const query = clampSearchQuery(inputValue).trim();
    setSuggestionsOpen(false);
    scheduleLexical.cancel();
    trackEvent("archive_search_submit", {
      token_count: normalizeSearchText(query).split(" ").filter(Boolean).length,
      selected_tags: (state.selectedTags || []).length,
      mode: ARCHIVE_SEARCH_MODES.SEMANTIC,
    });
    if (!shouldSubmitSemanticSearch(query)) {
      runLexical(query);
      return;
    }
    // A meaning search starts over: a tag picked earlier would only filter its keyword half.
    runSearch({ query, selectedTags: [], mode: ARCHIVE_SEARCH_MODES.SEMANTIC, sort: state.sort }, { semantic: "force" }).catch(() => {});
  }, [inputValue, runLexical, runSearch, scheduleLexical, state.selectedTags, state.sort]);

  const clearTags = useCallback(() => {
    scheduleLexical.cancel();
    runLexical(inputValue, []);
  }, [inputValue, runLexical, scheduleLexical]);

  const selectTag = useCallback((tagSlug) => {
    const selected = new Set(state.selectedTags || []);
    selected.add(tagSlug);
    const nextTags = [...selected];
    setSuggestionsOpen(false);
    scheduleLexical.cancel();
    runLexical(inputValue, nextTags);
  }, [inputValue, runLexical, scheduleLexical, state.selectedTags]);

  const removeTag = useCallback((tagSlug) => {
    const nextTags = (state.selectedTags || []).filter((slug) => slug !== tagSlug);
    scheduleLexical.cancel();
    runLexical(inputValue, nextTags);
  }, [inputValue, runLexical, scheduleLexical, state.selectedTags]);

  const selectSuggestion = useCallback((suggestion) => {
    if (!suggestion) return;
    if (suggestion.type === "tag") {
      selectTag(suggestion.slug);
      return;
    }
    setSuggestionsOpen(false);
    setActiveSuggestion(-1);
    scheduleLexical.cancel();
    if (suggestion.type === "shows-like") {
      const query = `Shows like ${suggestion.showTitle}`;
      setInputValue(query);
      trackEvent("archive_search_shows_like", { show_slug: suggestion.slug });
      runSearch({ query, selectedTags: [], mode: ARCHIVE_SEARCH_MODES.SEMANTIC, sort: state.sort }, { semantic: "force" }).catch(() => {});
      return;
    }
    // Picking one show is a new search, so an earlier tag does not narrow it.
    setInputValue(suggestion.label);
    runLexical(suggestion.label, []);
  }, [runLexical, runSearch, scheduleLexical, selectTag, state.sort]);

  const onInputChange = (event) => {
    const nextQuery = clampSearchQuery(event.target.value);
    setInputValue(nextQuery);
    setSuggestionsOpen(true);
    setActiveSuggestion(-1);
    scheduleLexical.run(nextQuery);
    if (!nextQuery && (state.selectedTags || []).length === 0) {
      scheduleLexical.cancel();
      clearSearch();
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSuggestionsOpen(true);
      setActiveSuggestion((current) => Math.min(current + 1, suggestions.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSuggestionsOpen(true);
      setActiveSuggestion((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Home" && suggestionsOpen) {
      event.preventDefault();
      setActiveSuggestion(0);
      return;
    }
    if (event.key === "End" && suggestionsOpen) {
      event.preventDefault();
      setActiveSuggestion(Math.max(0, suggestions.length - 1));
      return;
    }
    if (event.key === "Escape") {
      setSuggestionsOpen(false);
      setActiveSuggestion(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (suggestionsOpen && activeSuggestion >= 0) {
        selectSuggestion(suggestions[activeSuggestion]);
      } else {
        submitSemantic();
      }
      return;
    }
    if (event.key === "Tab") {
      setSuggestionsOpen(false);
    }
  };

  const changeSort = (sort) => {
    scheduleLexical.cancel();
    runSearch({ ...state, sort }, { semantic: state.mode === ARCHIVE_SEARCH_MODES.SEMANTIC ? "force" : "never" }).catch(() => {});
  };

  const emptyStateTags = popularTags.filter((tag) => !(state.selectedTags || []).includes(tag.slug)).slice(0, 4);

  const statusText = searchStatus === "semantic-loading"
    ? "Searching by meaning"
    : isSearchActive
      ? `${results.length} ${results.length === 1 ? "result" : "results"}`
      : "Archive search ready";

  return (
    <section className="archive-discovery mb-8 space-y-5" aria-label="Search the archive">
      <div className="archive-discovery__search">
        <div className="space-y-3">
          <ArchiveSearchInput
            activeSuggestion={activeSuggestion}
            canSubmitSearch={canSubmitSearch}
            clearSearchInput={clearSearchInput}
            comboboxId={comboboxId}
            discoveryPrompt={discoveryPrompt}
          inputRef={inputRef}
          inputValue={inputValue}
          listboxId={listboxId}
          onInputChange={onInputChange}
          onKeyDown={onKeyDown}
          prefetchIndex={prefetchIndex}
          placeholder={`Try "${placeholderPrompt}"`}
          searchStatus={searchStatus}
          selectSuggestion={selectSuggestion}
          setActiveSuggestion={setActiveSuggestion}
          setSuggestionsOpen={setSuggestionsOpen}
          showSuggestions={showSuggestions}
          statusId={statusId}
          surpriseMeDisabled={!index || indexStatus === "loading"}
          surpriseMeLabel="Surprise me"
          onSurpriseMe={surpriseMe}
          submitSemantic={submitSemantic}
          suggestions={suggestions}
        />
        </div>

        <ArchiveSearchTagBar
          indexStatus={indexStatus}
          isSearchActive={isSearchActive}
          popularTags={popularTags}
          prefetchIndex={prefetchIndex}
          removeTag={removeTag}
          selectTag={selectTag}
          selectedTagDetails={selectedTagDetails}
        />
      </div>

      <div id={statusId} className="sr-only" aria-live="polite">
        {statusText}
      </div>

      {isSearchActive ? (
        <ArchiveSearchResults
          changeSort={changeSort}
          clearSearchInput={clearSearchInput}
          clearTags={clearTags}
          emptyStateTags={emptyStateTags}
          fallbackReason={fallbackReason}
          isInWatchlist={isInWatchlist}
          results={results}
          searchResult={searchResult}
          searchStatus={searchStatus}
          selectTag={selectTag}
          selectedTagDetails={selectedTagDetails}
          semanticFallback={semanticFallback}
          showBlockingLoading={showBlockingLoading}
          showsLikeTitle={showsLikeAnchor?.title || ""}
          state={state}
          submitSemantic={submitSemantic}
          toggleWatchlist={toggleWatchlist}
          unknownSemanticSlugs={unknownSemanticSlugs}
        />
      ) : (
        <div className="space-y-4">
          <div className="space-y-1">
            <Typography as="h2" variant="h3" className="text-foreground">
              ...or browse by month
            </Typography>
            <Typography variant="body-small" className="text-muted-foreground">
              Open a completed month to see every show listed in it.
            </Typography>
          </div>
          {children}
        </div>
      )}
    </section>
  );
};

ArchiveDiscoverySearch.propTypes = {
  children: PropTypes.node.isRequired,
  search: PropTypes.string,
};

export default ArchiveDiscoverySearch;
